import { useSyncExternalStore } from 'react';

import { type FlagKey } from './registry';

// Which flags are rendering on screen right now, and what they read, for the floating legend
// (FeatureFlagLegend) and the borders Flagged draws. useFeatureFlag registers every read it
// makes and Flagged registers every region it marks. The registry runs everywhere; the legend
// decides where it shows.

export type FlagReadState = 'on' | 'off' | 'mixed';
/** `region` is a read or a marked piece of UI; `page` is a flag that changes the whole screen. */
export type FlagReadScope = 'region' | 'page';
export type FlagRead = { key: FlagKey; state: FlagReadState; page: boolean };

type Counts = { on: number; off: number; page: number };
const mounted = new Map<FlagKey, Counts>();
const listeners = new Set<() => void>();
let snapshot: readonly FlagRead[] = [];

const stateOf = ({ on, off }: Counts): FlagReadState =>
  on > 0 && off > 0 ? 'mixed' : on > 0 ? 'on' : 'off';
const shapeOf = (counts: Counts) => `${stateOf(counts)}:${counts.page > 0}`;

const publish = () => {
  snapshot = [...mounted.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, counts]) => ({ key, state: stateOf(counts), page: counts.page > 0 }));
  listeners.forEach((listener) => listener());
};

/** Counts one mounted read of `key` that read `value`, and returns its release. */
export function registerFlagRead(
  key: FlagKey,
  value: boolean,
  scope: FlagReadScope = 'region',
): () => void {
  const counts = mounted.get(key) ?? { on: 0, off: 0, page: 0 };
  const side = value ? 'on' : 'off';
  const before = mounted.has(key) ? shapeOf(counts) : undefined;
  counts[side] += 1;
  if (scope === 'page') counts.page += 1;
  mounted.set(key, counts);
  if (shapeOf(counts) !== before) publish();
  return () => {
    const was = shapeOf(counts);
    counts[side] -= 1;
    if (scope === 'page') counts.page -= 1;
    if (counts.on === 0 && counts.off === 0) {
      mounted.delete(key);
      publish();
    } else if (shapeOf(counts) !== was) {
      publish();
    }
  };
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getSnapshot = () => snapshot;

/** The flags rendering on screen, sorted by key, each with what was read. */
export const useFlagReads = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
